import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { IdCard, Loader2, Search, CheckSquare, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { UseTemplateDialog } from "@/components/UseTemplateDialog";

export default function IDCards() {
  const { user } = useAuth();
  const [audience, setAudience] = useState("student");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [generating, setGenerating] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const canPrint = hasPerm(user, "print");
  const isStaff = audience === "staff";

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);
  const classNames = [...new Set(classes.map((c) => (c.name || "").trim()).filter(Boolean))];

  useEffect(() => {
    setLoading(true);
    setSelected([]);
    if (isStaff) {
      api.get("/staff").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    } else {
      const params = { limit: 1000 };
      if (classId !== "all") params.class_name = classId;
      api.get("/students", { params }).then((r) => setRows(r.data.students || [])).catch(() => setRows([])).finally(() => setLoading(false));
    }
  }, [audience, classId]); // eslint-disable-line

  const photoOf = (r) => isStaff ? r.photo : (r.docs && r.docs.photo);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const hay = isStaff
      ? [r.name, r.staff_id, r.designation, r.phone].filter(Boolean).join(" ")
      : [r.name, r.admission_number, r.roll_number, r.class_name].filter(Boolean).join(" ");
    return hay.toLowerCase().includes(q);
  });

  const allSelected = filtered.length > 0 && filtered.every((r) => selected.includes(r.id));
  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map((r) => r.id));

  const generate = async () => {
    const chosen = selected.length ? rows.filter((r) => selected.includes(r.id)) : filtered;
    const ids = chosen.map((r) => r.id);
    if (!ids.length) { toast.error("No records to print"); return; }
    const missing = chosen.filter((r) => !photoOf(r)).length;
    if (missing > 0 && !window.confirm(`${missing} of ${ids.length} record(s) have no photo — their ID cards will show a placeholder. Continue?`)) return;
    setGenerating(true);
    try {
      const url = isStaff ? "/staff/idcards/batch" : "/students/idcards/batch";
      const body = isStaff ? { staff_ids: ids } : { student_ids: ids };
      const r = await api.post(url, body, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
      toast.success(`Generated ${ids.length} ID card(s)`);
    } catch (e) { toast.error("Could not generate ID cards"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><IdCard className="text-indigo-500" size={26} />ID Cards</h1>
          <p className="text-slate-500">Choose who to print for, pick records, and generate printable ID cards.</p>
        </div>
        {canPrint && (
          <div className="flex gap-2">
            {!isStaff && (
              <Button variant="outline" onClick={() => { if (!(selected.length ? selected : filtered).length) { toast.error("No students to print"); return; } setTplOpen(true); }} className="gap-1.5" data-testid="idcards-use-template-btn">
                <LayoutTemplate size={16} />Use Ready Template
              </Button>
            )}
            <Button onClick={generate} disabled={generating} className="gap-1.5" data-testid="generate-idcards-btn">
              {generating ? <Loader2 size={16} className="animate-spin" /> : <IdCard size={16} />}
              Generate ID Cards{selected.length ? ` (${selected.length})` : ""}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Choose ID Card for</span>
          <Select value={audience} onValueChange={(v) => { setAudience(v); setSearch(""); }}>
            <SelectTrigger className="w-36" data-testid="idcards-audience-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder={isStaff ? "Search name, staff ID, designation…" : "Search name, admission no, roll…"} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="idcards-search" />
        </div>
        {!isStaff && (
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-56" data-testid="idcards-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="gap-1.5" onClick={toggleAll} data-testid="idcards-select-all"><CheckSquare size={15} />{allSelected ? "Unselect All" : "Select All"}</Button>
        <span className="text-xs text-slate-400 ml-auto">{selected.length} selected · {filtered.length} of {rows.length}</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><IdCard size={40} className="mx-auto mb-2 opacity-40" />No {isStaff ? "staff" : "students"} found.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 w-10"></th>
                  <th className="text-left px-4 py-3">Photo</th>
                  <th className="text-left px-4 py-3">Name</th>
                  {isStaff ? <>
                    <th className="text-left px-4 py-3">Staff ID</th>
                    <th className="text-left px-4 py-3">Designation</th>
                    <th className="text-left px-4 py-3">Phone</th>
                  </> : <>
                    <th className="text-left px-4 py-3">Admission No.</th>
                    <th className="text-left px-4 py-3">Roll</th>
                    <th className="text-left px-4 py-3">Class</th>
                  </>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(r.id)} data-testid={`idcard-row-${r.id}`}>
                    <td className="px-4 py-2.5"><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`idcard-check-${r.id}`} /></td>
                    <td className="px-4 py-2.5">
                      {photoOf(r) ? <img src={photoOf(r)} alt={r.name} className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">{initials(r.name)}</div>}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.name}</td>
                    {isStaff ? <>
                      <td className="px-4 py-2.5 text-slate-500">{r.staff_id || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.designation || r.role || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.phone || "—"}</td>
                    </> : <>
                      <td className="px-4 py-2.5 text-slate-500">{r.admission_number}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.roll_number || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.class_name}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {!isStaff && (
        <UseTemplateDialog
          open={tplOpen}
          onOpenChange={setTplOpen}
          categories={["Id Card"]}
          heading="Use a Ready ID Card Template"
          getPayload={() => ({ student_ids: (selected.length ? selected : filtered.map((r) => r.id)) })}
        />
      )}
    </div>
  );
}
